// Header-side AI mode controller.
// Chat itself lives on the dedicated /pages/ki-modus page; submitting in AI mode
// simply redirects there with the query as ?q=… parameter.
const KI_MODUS_PAGE = '/pages/ki-modus';

let aiModeActive = false;

const SELECTORS = {
  triggers: '.ll-ai-trigger',
  searchInputs: '#autocomplete-desktop .aa-Input, #autocomplete-mobile .aa-Input',
  searchForms: '#autocomplete-desktop .aa-Form, #autocomplete-mobile .aa-Form',
};

function sendQueryToChat(query) {
  window.location.href = `${KI_MODUS_PAGE}?q=${encodeURIComponent(query)}`;
}

const AI_PLACEHOLDER = 'Stelle deine Frage';
const FORM_RADIUS = '22px';

function enforcePlaceholder(input) {
  if (input.placeholder !== AI_PLACEHOLDER) {
    input.placeholder = AI_PLACEHOLDER;
  }
}

function enforceFormRadius(form) {
  if (form.style.borderRadius !== FORM_RADIUS) {
    form.style.setProperty('border-radius', FORM_RADIUS, 'important');
  }
}

function startAiEnforcement() {
  document.querySelectorAll(SELECTORS.searchInputs).forEach((input) => {
    if (input._aiPlaceholderObserver) return;
    if (input.dataset.originalPlaceholder === undefined) {
      input.dataset.originalPlaceholder = input.placeholder || '';
    }
    enforcePlaceholder(input);
    const obs = new MutationObserver(() => {
      if (aiModeActive) enforcePlaceholder(input);
    });
    obs.observe(input, { attributes: true, attributeFilter: ['placeholder'] });
    input._aiPlaceholderObserver = obs;
  });

  document.querySelectorAll(SELECTORS.searchForms).forEach((form) => {
    if (form._aiFormObserver) return;
    enforceFormRadius(form);
    const obs = new MutationObserver(() => {
      if (aiModeActive) enforceFormRadius(form);
    });
    obs.observe(form, { attributes: true, attributeFilter: ['style'] });
    form._aiFormObserver = obs;
  });
}

function stopAiEnforcement() {
  document.querySelectorAll(SELECTORS.searchInputs).forEach((input) => {
    if (input._aiPlaceholderObserver) {
      input._aiPlaceholderObserver.disconnect();
      delete input._aiPlaceholderObserver;
    }
    if (input.dataset.originalPlaceholder !== undefined) {
      input.placeholder = input.dataset.originalPlaceholder;
    }
  });
  document.querySelectorAll(SELECTORS.searchForms).forEach((form) => {
    if (form._aiFormObserver) {
      form._aiFormObserver.disconnect();
      delete form._aiFormObserver;
    }
  });
}

const GLOW_ANIMATIONS = new WeakMap();
const GLOW_KEYFRAMES = [
  { boxShadow: '0 0 0 0 rgba(1, 217, 152, 0)',     offset: 0 },
  { boxShadow: '0 0 0 4px rgba(1, 217, 152, 0.55)', offset: 0.2 },
  { boxShadow: '0 0 0 8px rgba(35, 182, 165, 0.45)', offset: 0.5 },
  { boxShadow: '0 0 0 14px rgba(76, 141, 180, 0.25)', offset: 0.75 },
  { boxShadow: '0 0 0 20px rgba(104, 113, 191, 0)',  offset: 1 },
];
const GLOW_OPTIONS = { duration: 1400, easing: 'ease-out', fill: 'none' };

function triggerGlow(form) {
  const prev = GLOW_ANIMATIONS.get(form);
  if (prev) prev.cancel();
  const anim = form.animate(GLOW_KEYFRAMES, GLOW_OPTIONS);
  GLOW_ANIMATIONS.set(form, anim);
  anim.onfinish = () => GLOW_ANIMATIONS.delete(form);
}

function cancelGlow(form) {
  const prev = GLOW_ANIMATIONS.get(form);
  if (prev) prev.cancel();
  GLOW_ANIMATIONS.delete(form);
}

function setAiMode(active) {
  aiModeActive = active;
  document.body.classList.toggle('ll-ai-mode-active', active);

  if (active) {
    startAiEnforcement();
    document.querySelectorAll('#autocomplete-desktop, #autocomplete-mobile').forEach(triggerGlow);
  } else {
    stopAiEnforcement();
    document.querySelectorAll('#autocomplete-desktop, #autocomplete-mobile').forEach(cancelGlow);
  }

  document.querySelectorAll(SELECTORS.triggers).forEach((btn) => {
    btn.classList.toggle('ll-ai-trigger--active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

function setupAiTriggers() {
  document.querySelectorAll(SELECTORS.triggers).forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      setAiMode(!aiModeActive);
      if (aiModeActive) {
        const input = document.querySelector(SELECTORS.searchInputs);
        input?.focus();
      }
    });
  });
}

function setupSubmitInterceptor() {
  document.addEventListener('submit', (e) => {
    if (!aiModeActive) return;
    const form = e.target;
    if (!form.matches(SELECTORS.searchForms)) return;
    e.preventDefault();
    e.stopPropagation();
    const input = form.querySelector('input, textarea');
    const query = input?.value?.trim();
    if (query) sendQueryToChat(query);
  }, true);

  document.addEventListener('keydown', (e) => {
    if (!aiModeActive) return;
    if (e.key !== 'Enter') return;
    const target = e.target;
    if (!target.matches(SELECTORS.searchInputs)) return;
    e.preventDefault();
    e.stopPropagation();
    const query = target.value?.trim();
    if (query) sendQueryToChat(query);
  }, true);
}

function init() {
  setupAiTriggers();
  setupSubmitInterceptor();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
